import { Icon } from './Icons';
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { apiPost } from '../api';

// ── WOD themes that rotate ────────────────────────────────────────────────────

const WOD_THEMES = [
  { id: 'strength_monday', label: 'Strength Monday', emoji: '🏋️', color: '#f0a050', day: 1 },
  { id: 'cardio_tuesday', label: 'Conditioning Tuesday', emoji: '🔥', color: '#ef4444', day: 2 },
  { id: 'hybrid_wednesday', label: 'Hybrid Wednesday', emoji: '⚡', color: '#00AEEF', day: 3 },
  { id: 'strength_thursday', label: 'Strength Thursday', emoji: '💪', color: '#f0a050', day: 4 },
  { id: 'metcon_friday', label: 'MetCon Friday', emoji: '🏃', color: '#22c55e', day: 5 },
  { id: 'endurance_saturday', label: 'Endurance Saturday', emoji: '🎯', color: '#a78bfa', day: 6 },
  { id: 'active_recovery', label: 'Active Recovery Sunday', emoji: '🧘', color: '#5a7a90', day: 0 },
];

function todayStr() { return new Date().toISOString().split('T')[0]; }
function getDayOfWeek() { return new Date().getDay(); }

async function loadWOD() {
  const today = todayStr();
  const snap = await getDoc(doc(db, 'wod', today));
  return snap.exists() ? snap.data() : null;
}

async function saveWOD(data) {
  const today = todayStr();
  await setDoc(doc(db, 'wod', today), { ...data, generatedAt: serverTimestamp() });
}

function ExerciseRow({ exercise, index, accentColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', padding: '.75rem .9rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.4rem', border: '1px solid var(--border2)' }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: accentColor + '18', border: '1px solid ' + accentColor + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, color: accentColor, flexShrink: 0 }}>{index + 1}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--text)' }}>{exercise.name}</div>
        {exercise.description && <div style={{ fontSize: '.72rem', color: 'var(--text3)', marginTop: '.1rem', fontWeight: 300 }}>{exercise.description}</div>}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '.82rem', fontWeight: 600, color: accentColor }}>{exercise.prescription}</div>
        {exercise.rest && <div style={{ fontSize: '.65rem', color: 'var(--text3)', marginTop: '.1rem' }}>Rest: {exercise.rest}</div>}
      </div>
    </div>
  );
}

export default function WOD() {
  const [wod, setWod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [timerVal, setTimerVal] = useState(0);
  const [timerRef] = useState({ current: null });

  const dayOfWeek = getDayOfWeek();
  const todayTheme = WOD_THEMES.find(t => t.day === dayOfWeek) || WOD_THEMES[0];

  useEffect(() => {
    loadWOD().then(data => {
      if (data?.wod) {
        setWod(data.wod);
        setLoading(false);
      } else {
        setLoading(false);
        generateWOD();
      }
    });
    return () => clearInterval(timerRef.current);
  }, []);

  function startTimer(seconds) {
    setTimerVal(seconds);
    setTimerActive(true);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimerVal(v => {
        if (v <= 1) { clearInterval(timerRef.current); setTimerActive(false); return 0; }
        return v - 1;
      });
    }, 1000);
  }

  async function generateWOD() {
    setGenerating(true);

    const prompt = `You are an elite fitness coach. Generate the Levare Team Workout of the Day for ${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}.

Today's theme: ${todayTheme.label}

RULES:
- This is a shared team workout — it must be scalable for all fitness levels
- Include 3 scaling options (Beginner / Intermediate / Advanced) for the main work
- MUST include a proper warm-up (5-7 min)
- MUST include core work
- ${todayTheme.id.includes('cardio') || todayTheme.id.includes('metcon') ? 'This is a conditioning day — include intervals, circuits or AMRAP format.' : ''}
- ${todayTheme.id.includes('strength') ? 'This is a strength day — include heavy compound movements with proper sets/reps/RPE.' : ''}
- ${todayTheme.id.includes('endurance') ? 'This is an endurance day — longer duration, aerobic focus.' : ''}
- ${todayTheme.id.includes('recovery') ? 'This is active recovery — light movement, mobility, yoga-style flows, nothing intense.' : ''}
- ${todayTheme.id.includes('hybrid') ? 'This is a hybrid day — mix of strength and conditioning.' : ''}
- Include a motivational "Team Note" — one sentence that fires people up
- Make it feel like a premium, world-class team workout
- No equipment required for cardio/metcon days. Barbell + dumbbell available for strength days.
- Include a difficulty score out of 10

Return ONLY valid JSON, no markdown:
{
  "title": "string — creative workout name",
  "theme": "${todayTheme.label}",
  "date": "${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}",
  "difficulty": 7,
  "estimatedTime": "45 min",
  "teamNote": "One line that fires people up",
  "format": "string — e.g. 5 rounds for time / EMOM 20 min / Straight sets",
  "warmup": {
    "duration": "6 min",
    "exercises": ["Exercise — duration/reps", "Exercise 2"]
  },
  "mainWork": [
    {
      "name": "Exercise name",
      "prescription": "4 sets × 6 reps @ RPE 8",
      "description": "Brief description or cue",
      "rest": "2 min"
    }
  ],
  "coreFinisher": [
    {
      "name": "Core exercise",
      "prescription": "3 × 45 seconds",
      "description": "Cue",
      "rest": "15 seconds"
    }
  ],
  "scaling": {
    "beginner": "How to scale down for beginners",
    "intermediate": "Standard as written",
    "advanced": "How to increase difficulty"
  },
  "cooldown": ["Stretch 1 — 30s", "Stretch 2 — 30s", "Stretch 3 — 30s"]
}`;

    try {
      const data = await apiPost('/api/pt', {
        messages: [{ role: 'user', content: prompt }],
        trainingProfile: null, liftLog: '', workingWeights: '', healthData: null
      });
      const content = data.content || '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        setWod(parsed);
        await saveWOD({ wod: parsed });
      }
    } catch (e) {
      console.error('WOD generation error:', e);
    }
    setGenerating(false);
  }

  if (loading) return <div style={{ padding: '2rem 0', color: 'var(--text3)', fontSize: '.82rem' }}>Loading today's workout…</div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.3rem' }}>
            <div style={{ padding: '.3rem .75rem', borderRadius: 20, background: todayTheme.color + '15', border: '1px solid ' + todayTheme.color + '35', fontSize: '.7rem', fontWeight: 600, color: todayTheme.color, textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Levare WOD
            </div>
            <div style={{ fontSize: '.75rem', color: 'var(--text3)' }}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.5rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em' }}>
            {wod ? wod.title : todayTheme.label}
          </h2>
          {wod && <p style={{ fontSize: '.78rem', color: 'var(--text3)', marginTop: '.2rem', fontWeight: 300 }}>{wod.theme} · {wod.estimatedTime}{wod.format ? ` · ${wod.format}` : ''}</p>}
        </div>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          {wod?.difficulty && (
            <div style={{ padding: '.35rem .85rem', borderRadius: 20, background: 'var(--bg-elevated)', border: '1px solid var(--border2)', fontSize: '.72rem', color: 'var(--text3)' }}>
              Difficulty: <span style={{ fontWeight: 700, color: wod.difficulty >= 8 ? 'var(--danger)' : wod.difficulty >= 6 ? 'var(--warning)' : 'var(--success)' }}>{wod.difficulty}/10</span>
            </div>
          )}
          <button onClick={generateWOD} disabled={generating} style={{ padding: '.4rem 1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.75rem', cursor: generating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '.4rem', opacity: generating ? .6 : 1 }}>
            <span style={{ display: 'inline-block', animation: generating ? 'spin .8s linear infinite' : 'none' }}>↻</span>
            {generating ? 'Generating…' : wod ? 'Refresh WOD' : 'Generate WOD'}
          </button>
        </div>
      </div>

      {generating && (
        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: `3px solid ${todayTheme.color}30`, borderTop: `3px solid ${todayTheme.color}`, animation: 'spin .8s linear infinite', margin: '0 auto 1rem' }} />
          <div style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Rico is building today's team workout…</div>
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}

      {!wod && !generating && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', border: `3px solid ${todayTheme.color}30`, borderTop: `3px solid ${todayTheme.color}`, animation: 'spin .8s linear infinite', margin: '0 auto 1rem' }} />
          <div style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Loading today's workout…</div>
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}

      {wod && !generating && (
        <div style={{ animation: 'fadeIn .3s ease' }}>

          {/* Team note */}
          {wod.teamNote && (
            <div style={{ padding: '1rem 1.25rem', background: 'linear-gradient(135deg, ' + todayTheme.color + '12, ' + todayTheme.color + '06)', border: '1px solid ' + todayTheme.color + '30', borderRadius: 'var(--radius-lg)', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '.85rem' }}>
              <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>🔥</div>
              <div style={{ fontFamily: 'var(--font-head)', fontSize: '.95rem', color: todayTheme.color, fontWeight: 500, lineHeight: 1.4 }}>{wod.teamNote}</div>
            </div>
          )}

          {/* Timer */}
          {timerActive && (
            <div style={{ padding: '.75rem 1.25rem', background: 'var(--warning-dim)', border: '1px solid rgba(240,160,80,.3)', borderRadius: 'var(--radius)', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '.8rem', color: 'var(--warning)' }}>Rest timer</span>
              <span style={{ fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 700, color: 'var(--warning)' }}>{Math.floor(timerVal / 60)}:{String(timerVal % 60).padStart(2, '0')}</span>
            </div>
          )}

          {/* Warm up */}
          {wod.warmup && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.65rem' }}>Warm-up · {wod.warmup.duration}</div>
              {(wod.warmup.exercises || []).map((ex, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.25rem 0', borderBottom: i < wod.warmup.exercises.length - 1 ? '1px solid var(--border2)' : 'none' }}>• {ex}</div>
              ))}
            </div>
          )}

          {/* Main work */}
          {wod.mainWork && wod.mainWork.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: todayTheme.color, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Main Work{wod.format ? ' — ' + wod.format : ''}</div>
              {wod.mainWork.map((ex, i) => <ExerciseRow key={i} exercise={ex} index={i} accentColor={todayTheme.color} />)}
            </div>
          )}

          {/* Core finisher */}
          {wod.coreFinisher && wod.coreFinisher.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Core Finisher</div>
              {wod.coreFinisher.map((ex, i) => <ExerciseRow key={i} exercise={ex} index={i} accentColor="#a78bfa" />)}
            </div>
          )}

          {/* Scaling */}
          {wod.scaling && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>Scaling Options</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                {[['Beginner', wod.scaling.beginner, 'var(--success)'], ['Intermediate', wod.scaling.intermediate, 'var(--warning)'], ['Advanced', wod.scaling.advanced, 'var(--danger)']].map(([level, desc, color]) => (
                  <div key={level} style={{ display: 'flex', gap: '.75rem', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '.65rem', padding: '.2rem .6rem', borderRadius: 20, background: color + '15', color, fontWeight: 600, flexShrink: 0, marginTop: '.1rem' }}>{level}</span>
                    <span style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.55 }}>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cool down */}
          {wod.cooldown && wod.cooldown.length > 0 && (
            <div style={{ background: 'rgba(0,174,239,.04)', border: '1px solid rgba(0,174,239,.15)', borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.65rem' }}>Cool-down</div>
              {wod.cooldown.map((s, i) => (
                <div key={i} style={{ fontSize: '.8rem', color: 'var(--text2)', padding: '.25rem 0', borderBottom: i < wod.cooldown.length - 1 ? '1px solid var(--border2)' : 'none' }}>• {s}</div>
              ))}
            </div>
          )}

          {/* Complete button */}
          <button onClick={() => setCompleted(true)} disabled={completed} style={{ width: '100%', padding: '.85rem', borderRadius: 'var(--radius-lg)', border: 'none', background: completed ? 'var(--success)' : todayTheme.color, color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.92rem', fontWeight: 600, cursor: completed ? 'default' : 'pointer', transition: 'all .3s', marginTop: '.5rem' }}>
            {completed ? '✓ WOD Complete — great work!' : 'Mark WOD as Complete'}
          </button>
        </div>
      )}

      <style>{'@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }'}</style>
    </div>
  );
}
